import { clearVectrosApiTokenCache, AuthError, getVectrosResolvedScope } from './chunk-26O3XQW6.mjs';
export { AuthError, __resetVectrosApiTokenCacheForTest, clearVectrosApiTokenCache, getVectrosApiToken, getVectrosResolvedScope, isAuthError, setPartnerApiTokenAssumer, setPartnerApiTokenMinter } from './chunk-26O3XQW6.mjs';
export { PasswordStrengthMeter_default as PasswordStrengthMeter } from './chunk-GLX37UYF.mjs';
import { createContext, lazy, useState, useCallback, useEffect, useMemo, useContext, useId, Suspense, Component, useRef } from 'react';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Container, Stack, Typography, Card, CardContent, TextField, InputAdornment, IconButton, FormHelperText, CircularProgress, Button, Drawer, AppBar, Toolbar, Avatar, Menu, MenuItem, Tooltip, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, ToggleButtonGroup, ToggleButton, Chip, Link, Divider, FormControlLabel, Switch, FormControl, InputLabel, Select, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useIntl, FormattedMessage, IntlProvider as IntlProvider$1 } from 'react-intl';
import { useLocation, Navigate, useNavigate, Outlet, NavLink } from 'react-router';
import MenuIcon from '@mui/icons-material/Menu';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { QRCodeSVG } from 'qrcode.react';

// src/i18n/messages.en.json
var messages_en_default = {
  "error.requestId": "Reference ID: {requestId}",
  "layout.account": "Account",
  "layout.loadingSession": "Loading session",
  "layout.navKeys": "Scoped Keys",
  "layout.navLogs": "Logs",
  "layout.navMembers": "Members",
  "layout.navWelcome": "Welcome",
  "layout.openNav": "Open navigation",
  "layout.primaryNav": "Primary",
  "layout.signOut": "Sign out",
  "layout.signedInAs": "Signed in as",
  "layout.skipToContent": "Skip to main content",
  "layout.tenantSwitcherLabel": "Switch tenant",
  "layout.userMenuLabel": "Open user menu",
  "mfa.cardTitle": "Two-factor authentication",
  "mfa.disableCancel": "Cancel",
  "mfa.disableConfirmBody": "You'll no longer be asked for a code at sign-in. You can turn it back on anytime.",
  "mfa.disableConfirmTitle": "Turn off two-factor authentication?",
  "mfa.disabledBody": "Add a one-time code from an authenticator app to your sign-in for an extra layer of security.",
  "mfa.enabledBody": "An authenticator app is protecting your account \u2014 you'll be asked for a one-time code at sign-in.",
  "mfa.enrollCancel": "Cancel",
  "mfa.enrollCodeLabel": "6-digit code",
  "mfa.enrollCopied": "Copied",
  "mfa.enrollCopySecret": "Copy key",
  "mfa.enrollDialogTitle": "Set up authenticator app",
  "mfa.enrollManualLabel": "Can't scan? Enter this key manually:",
  "mfa.enrollScanInstruction": "Scan this QR code with an authenticator app (Google Authenticator, 1Password, Authy, etc.), then enter the 6-digit code it shows.",
  "mfa.enrollVerify": "Verify & enable",
  "mfa.enrollVerifying": "Verifying\u2026",
  "mfa.loadError": "Couldn't load your security settings. Please refresh and try again.",
  "mfa.loading": "Loading security settings",
  "mfa.preparing": "Preparing setup",
  "mfa.qrTitle": "Authenticator setup QR code",
  "mfa.setUpCta": "Set up authenticator app",
  "mfa.statusDisabled": "Not enabled",
  "mfa.statusEnabled": "Enabled",
  "mfa.turnOffCta": "Turn off",
  "password.capsLockOn": "Caps Lock is on",
  "password.strengthFair": "Fair",
  "password.strengthGood": "Good",
  "password.strengthLabel": "Password strength",
  "password.strengthStrong": "Strong",
  "password.strengthVeryWeak": "Very weak",
  "password.strengthWeak": "Weak",
  "password.visibilityToggleHide": "Hide password",
  "password.visibilityToggleShow": "Show password",
  "recordForm.enumNone": "(none)",
  "recordForm.errorEnum": "Choose one of the allowed values.",
  "recordForm.errorRequired": "This field is required.",
  "recordForm.rawOnlyNote": "These fields can only be edited in the raw JSON view:"
};
var AuthContext = createContext(null);
function AuthProvider({ provider, children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshUser = useCallback(async () => {
    const current = await provider.getCurrentUser();
    setUser(current);
  }, [provider]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const current = await provider.getCurrentUser();
        if (!cancelled) setUser(current);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [provider]);
  const signOut = useCallback(async () => {
    await provider.signOut();
    clearVectrosApiTokenCache();
    setUser(null);
  }, [provider]);
  const getIdToken = useCallback(() => provider.getIdToken(), [provider]);
  const hasEmbedded = typeof provider.signIn === "function";
  const signIn = useCallback(
    async (input) => {
      const result = await provider.signIn(input);
      if (result.kind === "COMPLETE") {
        await refreshUser();
      }
      return result;
    },
    [provider, refreshUser]
  );
  const confirmSignIn = useCallback(
    async (input) => {
      const result = await provider.confirmSignIn(input);
      if (result.kind === "COMPLETE") {
        await refreshUser();
      }
      return result;
    },
    [provider, refreshUser]
  );
  const signUp = useCallback(
    (input) => provider.signUp(input),
    [provider]
  );
  const confirmSignUp = useCallback(
    (input) => provider.confirmSignUp(input),
    [provider]
  );
  const resendSignUpCode = useCallback(
    (input) => provider.resendSignUpCode(input),
    [provider]
  );
  const forgotPassword = useCallback(
    (input) => provider.forgotPassword(input),
    [provider]
  );
  const confirmForgotPassword = useCallback(
    (input) => provider.confirmForgotPassword(input),
    [provider]
  );
  const changePassword = useCallback(
    (input) => provider.changePassword(input),
    [provider]
  );
  const getMfaStatus = useCallback(() => provider.getMfaStatus(), [provider]);
  const setUpTotp = useCallback(() => provider.setUpTotp(), [provider]);
  const verifyTotpSetup = useCallback(
    (code) => provider.verifyTotpSetup(code),
    [provider]
  );
  const disableTotp = useCallback(() => provider.disableTotp(), [provider]);
  const hasHosted = typeof provider.signInWithRedirect === "function";
  const signInWithRedirect = useCallback(
    (options) => provider.signInWithRedirect(options),
    [provider]
  );
  const handleRedirectCallback = useCallback(async () => {
    await provider.handleRedirectCallback();
    await refreshUser();
  }, [provider, refreshUser]);
  const acceptInvite = useCallback(
    (inviteToken) => provider.acceptInvite(inviteToken),
    [provider]
  );
  const value = useMemo(() => {
    const core = { user, loading, isAuthenticated: user !== null, signOut, getIdToken };
    const embedded = hasEmbedded ? {
      signIn,
      confirmSignIn,
      signUp,
      confirmSignUp,
      resendSignUpCode,
      forgotPassword,
      confirmForgotPassword,
      changePassword,
      getMfaStatus,
      setUpTotp,
      verifyTotpSetup,
      disableTotp
    } : {};
    const hosted = hasHosted ? { signInWithRedirect, handleRedirectCallback, acceptInvite } : {};
    return { ...core, ...embedded, ...hosted };
  }, [
    user,
    loading,
    signOut,
    getIdToken,
    hasEmbedded,
    signIn,
    confirmSignIn,
    signUp,
    confirmSignUp,
    resendSignUpCode,
    forgotPassword,
    confirmForgotPassword,
    changePassword,
    getMfaStatus,
    setUpTotp,
    verifyTotpSetup,
    disableTotp,
    hasHosted,
    signInWithRedirect,
    handleRedirectCallback,
    acceptInvite
  ]);
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value, children });
}
function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

// src/auth/assertFacet.ts
function assertEmbeddedAuth(value) {
  if (typeof value.signIn !== "function") {
    throw new Error(
      "assertEmbeddedAuth: the configured AuthProvider does not implement EmbeddedCredentialAuth (no signIn method present). This usually means <AuthProvider> was wired with a hosted-redirect provider (e.g. Auth0AuthProvider) but the app code assumes an embedded-credential one (e.g. CognitoAuthProvider) \u2014 check the provider constructed in main.tsx."
    );
  }
}
function assertHostedAuth(value) {
  if (typeof value.signInWithRedirect !== "function") {
    throw new Error(
      "assertHostedAuth: the configured AuthProvider does not implement HostedRedirectAuth (no signInWithRedirect method present). This usually means <AuthProvider> was wired with an embedded-credential provider (e.g. CognitoAuthProvider) but the app code assumes a hosted-redirect one (e.g. Auth0AuthProvider) \u2014 check the provider constructed in main.tsx."
    );
  }
}

// src/auth/errorMessages.ts
function authErrorToMessage(intl, e) {
  if (e instanceof AuthError) {
    if (e.code === "PASSWORD_POLICY_VIOLATION" && e.message) return e.message;
    return intl.formatMessage({ id: `auth.errors.${e.code}` });
  }
  return intl.formatMessage({ id: "auth.errors.UNKNOWN" });
}
var CurrentTenantContext = createContext(null);
function useCurrentTenant() {
  const ctx = useContext(CurrentTenantContext);
  if (ctx) return ctx;
  return {
    tenant: null,
    setTenant: async () => void 0,
    memberships: [],
    loading: false,
    activeMembership: null,
    getActivePartnerUserId: async () => null,
    listAppContexts: async () => [],
    checkUserExists: async () => ({ exists: false, isMe: false }),
    linkInvitation: async () => {
      throw new Error(
        "useCurrentTenant: linkInvitation called outside a CurrentTenantProvider (or one with no tenancyProvider)."
      );
    }
  };
}
function useActiveTenantId() {
  const { tenant } = useCurrentTenant();
  if (tenant == null) {
    throw new Error(
      "useActiveTenantId: no active tenant. Tenant-scoped pages must render inside the AppLayout tenant gate (or, in tests, a CurrentTenantProvider seeded with initialTenant)."
    );
  }
  return tenant;
}

// src/auth/useScopeGate.ts
var OPS_LETTERS = "crudsx";
function isOpsString(s) {
  return s.length > 0 && [...s].every((c) => OPS_LETTERS.includes(c));
}
function canPerform(allowedActions, action) {
  if (allowedActions.includes("*")) return true;
  const askedSegs = action.split(":");
  if (askedSegs.length === 2 || askedSegs.length === 3) {
    const [resource, askedOps, askedQualifier] = askedSegs;
    if (resource && isOpsString(askedOps ?? "")) {
      let grantedOps = "";
      for (const raw of allowedActions) {
        const segs = raw.split(":");
        if (segs.length !== askedSegs.length) continue;
        const [grantedResource, grantedOpsStr, grantedQualifier] = segs;
        if (grantedResource !== resource) continue;
        if (askedSegs.length === 3 && grantedQualifier !== askedQualifier) continue;
        if (!isOpsString(grantedOpsStr ?? "")) continue;
        for (const c of grantedOpsStr ?? "") {
          if (!grantedOps.includes(c)) grantedOps += c;
        }
      }
      if (grantedOps) {
        return [...askedOps ?? ""].every((c) => grantedOps.includes(c));
      }
    }
  }
  return allowedActions.includes(action);
}
var EMPTY_ACTIONS = [];
var EMPTY_IDENTITY = {};
var EMPTY_RESOLVED = { allowedActions: EMPTY_ACTIONS, identity: EMPTY_IDENTITY };
function useScopeGate(tenantOverride) {
  const { tenant } = useCurrentTenant();
  const tenantId = tenantOverride ?? tenant;
  const [resolved, setResolved] = useState(null);
  useEffect(() => {
    if (tenantId == null) return;
    let cancelled = false;
    setResolved(null);
    getVectrosResolvedScope(tenantId).then((scope) => {
      if (!cancelled) setResolved(scope ?? EMPTY_RESOLVED);
    }).catch(() => {
      if (!cancelled) setResolved(EMPTY_RESOLVED);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);
  const allowed = resolved?.allowedActions ?? EMPTY_ACTIONS;
  const identity = resolved?.identity ?? EMPTY_IDENTITY;
  const can = (action) => canPerform(allowed, action);
  return {
    loading: resolved === null,
    allowedActions: allowed,
    identity,
    can
  };
}
function ScopeGate({
  action,
  fallback = null,
  children,
  tenantOverride
}) {
  const gate = useScopeGate(tenantOverride);
  if (gate.loading) return /* @__PURE__ */ jsx(Fragment, { children: null });
  return /* @__PURE__ */ jsx(Fragment, { children: gate.can(action) ? children : fallback });
}
function warnNoTenancyProvider(action) {
  console.warn(
    `CurrentTenantProvider: ${action} called with no tenancyProvider supplied \u2014 returning an empty/no-op result. This is very likely a wiring bug (a real mount forgot to pass tenancyProvider), not a legitimate empty state \u2014 see CurrentTenantProviderProps.tenancyProvider.`
  );
}
function CurrentTenantProvider({
  children,
  tenancyProvider,
  initialMemberships,
  initialTenant
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const seeded = initialMemberships !== void 0;
  const [memberships, setMemberships] = useState(
    initialMemberships ?? []
  );
  const [tenant, setTenantState] = useState(initialTenant ?? null);
  const [loading, setLoading] = useState(!seeded);
  const userSub = user?.sub ?? null;
  useEffect(() => {
    if (seeded) return;
    if (!tenancyProvider) {
      warnNoTenancyProvider("the membership load");
      setMemberships([]);
      setTenantState(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const [list, active] = await Promise.all([
          tenancyProvider.getMemberships(),
          tenancyProvider.getActiveTenant()
        ]);
        if (cancelled) return;
        setMemberships(list);
        const resolved = active && list.some((m) => m.tenantId === active) ? active : list[0]?.tenantId ?? null;
        setTenantState(resolved);
      } catch {
        if (!cancelled) {
          setMemberships([]);
          setTenantState(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [seeded, userSub, tenancyProvider]);
  const setTenant = useCallback(
    async (next) => {
      if (!tenancyProvider) {
        throw new Error("CurrentTenantProvider: cannot switch tenant \u2014 no tenancyProvider supplied.");
      }
      await tenancyProvider.setActiveTenant(next);
      clearVectrosApiTokenCache();
      setTenantState(next);
      await queryClient.invalidateQueries();
    },
    [tenancyProvider, queryClient]
  );
  const getActivePartnerUserId = useCallback(() => {
    if (!tenancyProvider) {
      warnNoTenancyProvider("getActivePartnerUserId");
      return Promise.resolve(null);
    }
    return tenancyProvider.getActivePartnerUserId();
  }, [tenancyProvider]);
  const listAppContexts = useCallback(
    (tenantId, options) => {
      if (!tenancyProvider?.listAppContexts) {
        if (!tenancyProvider) warnNoTenancyProvider("listAppContexts");
        return Promise.resolve([]);
      }
      return tenancyProvider.listAppContexts(tenantId, options);
    },
    [tenancyProvider]
  );
  const checkUserExists = useCallback(
    (email) => {
      if (!tenancyProvider) {
        warnNoTenancyProvider("checkUserExists");
        return Promise.resolve({ exists: false, isMe: false });
      }
      return tenancyProvider.checkUserExists(email);
    },
    [tenancyProvider]
  );
  const linkInvitation = useCallback(
    async (inviteToken) => {
      if (!tenancyProvider) {
        throw new Error("CurrentTenantProvider: cannot link an invitation \u2014 no tenancyProvider supplied.");
      }
      return tenancyProvider.linkInvitation(inviteToken);
    },
    [tenancyProvider]
  );
  const value = useMemo(
    () => ({
      tenant,
      setTenant,
      memberships,
      loading,
      activeMembership: memberships.find((m) => m.tenantId === tenant) ?? null,
      getActivePartnerUserId,
      listAppContexts,
      checkUserExists,
      linkInvitation
    }),
    [
      tenant,
      setTenant,
      memberships,
      loading,
      getActivePartnerUserId,
      listAppContexts,
      checkUserExists,
      linkInvitation
    ]
  );
  return /* @__PURE__ */ jsx(CurrentTenantContext.Provider, { value, children });
}
function AuthCard({
  brandName,
  title,
  subtitle,
  children,
  footer
}) {
  return /* @__PURE__ */ jsx(
    Box,
    {
      sx: {
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.default"
      },
      children: /* @__PURE__ */ jsx(
        Container,
        {
          component: "main",
          maxWidth: "xs",
          sx: {
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            py: { xs: 4, sm: 6 }
          },
          children: /* @__PURE__ */ jsxs(Stack, { alignItems: "stretch", spacing: 3, children: [
            /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "h5",
                component: "span",
                sx: { fontWeight: 700, textAlign: "center", display: "block" },
                children: brandName
              }
            ),
            /* @__PURE__ */ jsx(Card, { sx: { width: "100%" }, children: /* @__PURE__ */ jsx(CardContent, { sx: { p: { xs: 3, sm: 4 } }, children: /* @__PURE__ */ jsxs(Stack, { spacing: 3, children: [
              /* @__PURE__ */ jsxs(Box, { children: [
                /* @__PURE__ */ jsx(Typography, { variant: "h5", component: "h1", sx: { fontWeight: 700 }, children: title }),
                subtitle && /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 1 }, children: subtitle })
              ] }),
              children
            ] }) }) }),
            footer && /* @__PURE__ */ jsx(Box, { sx: { textAlign: "center" }, children: footer })
          ] })
        }
      )
    }
  );
}
var PasswordStrengthMeter = lazy(() => import('./PasswordStrengthMeter-7U3CX747.mjs'));
function PasswordField({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  required,
  disabled,
  error,
  helperText,
  autoComplete,
  showStrengthMeter = false,
  onStrengthChange
}) {
  const intl = useIntl();
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const meterId = `${inputId}-strength`;
  const [visible, setVisible] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const handleToggle = useCallback(() => {
    setVisible((v) => !v);
  }, []);
  const handleKey = useCallback((e) => {
    setCapsLockOn(e.getModifierState("CapsLock"));
  }, []);
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        id: inputId,
        name,
        label,
        type: visible ? "text" : "password",
        autoComplete,
        value,
        onChange,
        onBlur,
        onKeyDown: handleKey,
        onKeyUp: handleKey,
        required,
        disabled,
        error,
        helperText,
        fullWidth: true,
        slotProps: {
          input: {
            endAdornment: /* @__PURE__ */ jsx(InputAdornment, { position: "end", children: /* @__PURE__ */ jsx(
              IconButton,
              {
                "aria-label": intl.formatMessage({
                  id: visible ? "password.visibilityToggleHide" : "password.visibilityToggleShow"
                }),
                "aria-pressed": visible,
                onClick: handleToggle,
                edge: "end",
                disabled,
                size: "small",
                children: visible ? /* @__PURE__ */ jsx(VisibilityOffIcon, {}) : /* @__PURE__ */ jsx(VisibilityIcon, {})
              }
            ) }),
            // Wire aria-describedby when the meter is showing so screen
            // readers announce the strength alongside the input. Avoids
            // the situation where a visually-sighted user sees the meter
            // but an AT user doesn't.
            ...showStrengthMeter && value ? { "aria-describedby": meterId } : {}
          }
        }
      }
    ),
    capsLockOn && /* @__PURE__ */ jsx(FormHelperText, { role: "status", sx: { ml: 1.75, mt: 0.25 }, children: /* @__PURE__ */ jsx(FormattedMessage, { id: "password.capsLockOn" }) }),
    showStrengthMeter && /* @__PURE__ */ jsx(Suspense, { fallback: null, children: /* @__PURE__ */ jsx(
      PasswordStrengthMeter,
      {
        value,
        id: meterId,
        ...onStrengthChange ? { onStrengthChange } : {}
      }
    ) })
  ] });
}
function RequireAuth({
  children,
  redirectTo = "/login"
}) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const intl = useIntl();
  if (loading) {
    return /* @__PURE__ */ jsx(
      Box,
      {
        sx: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh"
        },
        children: /* @__PURE__ */ jsx(CircularProgress, { "aria-label": intl.formatMessage({ id: "layout.loadingSession" }) })
      }
    );
  }
  if (!isAuthenticated) {
    return /* @__PURE__ */ jsx(Navigate, { to: redirectTo, state: { from: location }, replace: true });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function RequireScope({
  action,
  children,
  redirectTo = "/",
  tenantOverride
}) {
  const gate = useScopeGate(tenantOverride);
  if (gate.loading) return /* @__PURE__ */ jsx(Fragment, { children: null });
  if (!gate.can(action)) return /* @__PURE__ */ jsx(Navigate, { to: redirectTo, replace: true });
  return /* @__PURE__ */ jsx(Fragment, { children });
}
var ErrorBoundary = class extends Component {
  state = { hasError: false };
  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("Render error", error, info.componentStack);
  }
  handleReload = () => {
    window.location.reload();
  };
  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return /* @__PURE__ */ jsx(Container, { maxWidth: "sm", sx: { py: 8 }, children: /* @__PURE__ */ jsxs(Stack, { spacing: 3, alignItems: "flex-start", children: [
      /* @__PURE__ */ jsx(Typography, { variant: "h4", component: "h1", children: "Something went wrong." }),
      /* @__PURE__ */ jsxs(Typography, { variant: "body1", color: "text.secondary", children: [
        "An unexpected error occurred. Please reload the page. If the problem continues, contact",
        " ",
        this.props.supportEmail,
        "."
      ] }),
      /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Button, { variant: "contained", onClick: this.handleReload, children: "Reload" }) })
    ] }) });
  }
};
var DRAWER_WIDTH = 220;
var RAIL_BG = "#09090B";
var RAIL_TEXT = "rgba(255, 255, 255, 0.72)";
var RAIL_HOVER_BG = "rgba(255, 255, 255, 0.08)";
var RAIL_ACTIVE_BG = "rgba(255, 255, 255, 0.14)";
var RAIL_DIVIDER = "rgba(255, 255, 255, 0.12)";
function NavList({ navItems, onItemSelect, scopeGateTenant }) {
  const intl = useIntl();
  return (
    // `component="nav"` makes this a navigation landmark (a11y); the aria-label
    // names it for screen readers and lets tests target the nav directly.
    /* @__PURE__ */ jsx(
      List,
      {
        component: "nav",
        "aria-label": intl.formatMessage({ id: "layout.primaryNav" }),
        sx: { px: 1, pt: 1 },
        children: navItems.map((item) => {
          const link = /* @__PURE__ */ jsx(ListItem, { disablePadding: true, children: /* @__PURE__ */ jsxs(
            ListItemButton,
            {
              component: NavLink,
              to: item.to,
              end: item.to === "/",
              onClick: onItemSelect,
              sx: {
                borderRadius: 1,
                mb: 0.5,
                color: RAIL_TEXT,
                "& .MuiListItemIcon-root": { color: "inherit", minWidth: 36 },
                "&:hover": { backgroundColor: RAIL_HOVER_BG, color: "common.white" },
                "&.active": {
                  backgroundColor: RAIL_ACTIVE_BG,
                  color: "common.white",
                  fontWeight: 700
                }
              },
              children: [
                item.icon != null && /* @__PURE__ */ jsx(ListItemIcon, { children: item.icon }),
                /* @__PURE__ */ jsx(
                  ListItemText,
                  {
                    primary: /* @__PURE__ */ jsx(FormattedMessage, { id: item.labelId }),
                    primaryTypographyProps: { fontSize: 14, fontWeight: "inherit" }
                  }
                )
              ]
            }
          ) }, item.to);
          if (item.gateAction === null) return link;
          return /* @__PURE__ */ jsx(
            ScopeGate,
            {
              action: item.gateAction,
              ...scopeGateTenant ? { tenantOverride: scopeGateTenant } : {},
              children: link
            },
            item.to
          );
        })
      }
    )
  );
}
function SidebarHeader({
  brandName,
  brandLogoSrc,
  brandQualifier,
  dense = false
}) {
  return /* @__PURE__ */ jsxs(
    Box,
    {
      component: NavLink,
      to: "/",
      sx: {
        display: "flex",
        alignItems: "center",
        minHeight: dense ? 48 : 64,
        px: 2.5,
        py: dense ? 0 : 2,
        textDecoration: "none",
        color: "inherit"
      },
      children: [
        brandLogoSrc ? /* @__PURE__ */ jsx(Box, { component: "img", src: brandLogoSrc, alt: brandName, sx: { height: 26, display: "block" } }) : /* @__PURE__ */ jsx(
          Typography,
          {
            variant: "h6",
            component: "span",
            sx: { fontWeight: 800, letterSpacing: "-0.02em", color: "common.white" },
            children: brandName
          }
        ),
        brandQualifier && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            Box,
            {
              "aria-hidden": true,
              sx: { width: "1px", alignSelf: "stretch", my: 1.25, ml: 0.25, mr: 1, bgcolor: "rgba(255, 255, 255, 0.2)" }
            }
          ),
          /* @__PURE__ */ jsx(
            Typography,
            {
              component: "span",
              sx: {
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255, 255, 255, 0.6)"
              },
              children: brandQualifier
            }
          )
        ] })
      ]
    }
  );
}
function AppLayout({
  brandName,
  brandLogoSrc,
  brandQualifier,
  navItems,
  switcher,
  appBarStart,
  appBarDense = false,
  scopeGateTenant
}) {
  const { user, signOut } = useAuth();
  const { loading: tenantLoading } = useCurrentTenant();
  const navigate = useNavigate();
  const intl = useIntl();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const userMenuId = useId();
  const userMenuLabel = intl.formatMessage({ id: "layout.userMenuLabel" });
  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };
  const handleSignOut = async () => {
    handleMenuClose();
    await signOut();
    navigate("/login", { replace: true });
  };
  const handleAccount = () => {
    handleMenuClose();
    navigate("/account");
  };
  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen((open) => !open);
  };
  const handleMobileNavItemSelect = () => {
    setMobileDrawerOpen(false);
  };
  const menuOpen = menuAnchor !== null;
  const avatarInitial = (user?.firstName?.[0] ?? user?.email?.[0] ?? "?").toUpperCase();
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const railPaperSx = {
    width: DRAWER_WIDTH,
    boxSizing: "border-box",
    backgroundColor: RAIL_BG,
    color: "common.white",
    borderRight: "none"
  };
  const railContent = (onItemSelect) => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      SidebarHeader,
      {
        brandName,
        ...brandLogoSrc ? { brandLogoSrc } : {},
        ...brandQualifier ? { brandQualifier } : {},
        ...appBarDense ? { dense: true } : {}
      }
    ),
    /* @__PURE__ */ jsx(Divider, { sx: { borderColor: RAIL_DIVIDER } }),
    /* @__PURE__ */ jsx(
      NavList,
      {
        navItems,
        ...onItemSelect ? { onItemSelect } : {},
        ...scopeGateTenant ? { scopeGateTenant } : {}
      }
    )
  ] });
  return /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", minHeight: "100vh", backgroundColor: "background.default" }, children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        component: "a",
        href: "#main-content",
        sx: {
          // Modern visually-hidden pattern — collapsed to a clipped 1px box until
          // focused, then revealed top-left.
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
          textDecoration: "none",
          "&:focus": {
            left: 8,
            top: 8,
            width: "auto",
            height: "auto",
            overflow: "visible",
            clipPath: "none",
            zIndex: (t) => t.zIndex.tooltip + 1,
            backgroundColor: "background.paper",
            color: "text.primary",
            padding: 1,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider"
          }
        },
        children: /* @__PURE__ */ jsx(FormattedMessage, { id: "layout.skipToContent" })
      }
    ),
    /* @__PURE__ */ jsx(
      Drawer,
      {
        variant: "permanent",
        sx: {
          width: DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": railPaperSx
        },
        open: true,
        children: railContent()
      }
    ),
    /* @__PURE__ */ jsx(
      Drawer,
      {
        variant: "temporary",
        open: mobileDrawerOpen,
        onClose: handleMobileDrawerToggle,
        ModalProps: { keepMounted: true },
        sx: {
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": railPaperSx
        },
        children: railContent(handleMobileNavItemSelect)
      }
    ),
    /* @__PURE__ */ jsxs(Box, { sx: { flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }, children: [
      /* @__PURE__ */ jsx(
        AppBar,
        {
          position: "static",
          elevation: 0,
          sx: {
            backgroundColor: "background.paper",
            color: "text.primary",
            borderBottom: "1px solid",
            borderColor: "divider"
          },
          children: /* @__PURE__ */ jsxs(Toolbar, { ...appBarDense ? { variant: "dense" } : {}, children: [
            /* @__PURE__ */ jsx(
              IconButton,
              {
                color: "inherit",
                edge: "start",
                onClick: handleMobileDrawerToggle,
                "aria-label": intl.formatMessage({ id: "layout.openNav" }),
                sx: { mr: 1, display: { xs: "inline-flex", md: "none" } },
                children: /* @__PURE__ */ jsx(MenuIcon, {})
              }
            ),
            appBarStart != null && appBarStart !== false && /* @__PURE__ */ jsx(Box, { sx: { display: "flex", alignItems: "center", minWidth: 0, mr: 2 }, children: appBarStart }),
            /* @__PURE__ */ jsx(Box, { sx: { flexGrow: 1 } }),
            user && /* @__PURE__ */ jsxs(Fragment, { children: [
              switcher && /* @__PURE__ */ jsx(Box, { sx: { mr: 1 }, children: switcher }),
              /* @__PURE__ */ jsx(
                IconButton,
                {
                  edge: "end",
                  onClick: handleMenuOpen,
                  "aria-label": userMenuLabel,
                  "aria-controls": menuOpen ? userMenuId : void 0,
                  "aria-haspopup": "true",
                  "aria-expanded": menuOpen,
                  children: /* @__PURE__ */ jsx(
                    Avatar,
                    {
                      sx: {
                        width: 32,
                        height: 32,
                        fontSize: 14,
                        backgroundColor: "primary.main",
                        color: "primary.contrastText"
                      },
                      children: avatarInitial
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsxs(
                Menu,
                {
                  id: userMenuId,
                  anchorEl: menuAnchor,
                  open: menuOpen,
                  onClose: handleMenuClose,
                  anchorOrigin: { vertical: "bottom", horizontal: "right" },
                  transformOrigin: { vertical: "top", horizontal: "right" },
                  slotProps: { list: { "aria-label": userMenuLabel } },
                  children: [
                    /* @__PURE__ */ jsxs(Box, { sx: { px: 2, py: 1 }, children: [
                      /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "text.secondary", component: "div", children: /* @__PURE__ */ jsx(FormattedMessage, { id: "layout.signedInAs" }) }),
                      fullName && /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: fullName }),
                      /* @__PURE__ */ jsx(
                        Typography,
                        {
                          variant: "body2",
                          color: fullName ? "text.secondary" : "text.primary",
                          children: user.email
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsx(MenuItem, { onClick: handleAccount, children: /* @__PURE__ */ jsx(FormattedMessage, { id: "layout.account" }) }),
                    /* @__PURE__ */ jsx(MenuItem, { onClick: handleSignOut, children: /* @__PURE__ */ jsx(FormattedMessage, { id: "layout.signOut" }) })
                  ]
                }
              )
            ] })
          ] })
        }
      ),
      /* @__PURE__ */ jsx(Box, { component: "main", id: "main-content", sx: { flexGrow: 1 }, children: /* @__PURE__ */ jsx(Container, { maxWidth: "lg", sx: { py: 4 }, children: tenantLoading ? /* @__PURE__ */ jsx(Box, { sx: { display: "flex", justifyContent: "center", py: 8 }, children: /* @__PURE__ */ jsx(CircularProgress, { "aria-label": intl.formatMessage({ id: "layout.loadingSession" }) }) }) : /* @__PURE__ */ jsx(Outlet, {}) }) })
    ] })
  ] });
}
var CODE_LENGTH = 6;
function TotpEnrollmentWizard({
  secret,
  otpauthUri,
  onVerify,
  pending = false,
  error = null,
  onCancel
}) {
  const intl = useIntl();
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2e3);
    } catch {
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length !== CODE_LENGTH || pending) return;
    onVerify(code);
  };
  return /* @__PURE__ */ jsx("form", { onSubmit: handleSubmit, noValidate: true, children: /* @__PURE__ */ jsxs(Stack, { spacing: 2, children: [
    /* @__PURE__ */ jsx(Typography, { variant: "body1", children: /* @__PURE__ */ jsx(FormattedMessage, { id: "mfa.enrollScanInstruction" }) }),
    /* @__PURE__ */ jsx(Box, { sx: { display: "flex", justifyContent: "center", py: 1 }, children: /* @__PURE__ */ jsx(
      QRCodeSVG,
      {
        value: otpauthUri,
        size: 180,
        title: intl.formatMessage({ id: "mfa.qrTitle" })
      }
    ) }),
    /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 0.5 }, children: /* @__PURE__ */ jsx(FormattedMessage, { id: "mfa.enrollManualLabel" }) }),
      /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [
        /* @__PURE__ */ jsx(
          Typography,
          {
            component: "code",
            sx: {
              fontFamily: "monospace",
              fontSize: 14,
              letterSpacing: 1,
              wordBreak: "break-all",
              bgcolor: "action.hover",
              px: 1,
              py: 0.5,
              borderRadius: 1
            },
            children: secret
          }
        ),
        /* @__PURE__ */ jsx(
          Tooltip,
          {
            title: intl.formatMessage({ id: copied ? "mfa.enrollCopied" : "mfa.enrollCopySecret" }),
            children: /* @__PURE__ */ jsx(
              IconButton,
              {
                size: "small",
                onClick: () => void handleCopy(),
                "aria-label": intl.formatMessage({ id: "mfa.enrollCopySecret" }),
                children: copied ? /* @__PURE__ */ jsx(CheckIcon, { fontSize: "small", color: "success" }) : /* @__PURE__ */ jsx(ContentCopyIcon, { fontSize: "small" })
              }
            )
          }
        )
      ] })
    ] }),
    error && /* @__PURE__ */ jsx(Alert, { severity: "error", role: "alert", children: error }),
    /* @__PURE__ */ jsx(
      TextField,
      {
        label: intl.formatMessage({ id: "mfa.enrollCodeLabel" }),
        value: code,
        onChange: (e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH)),
        disabled: pending,
        required: true,
        fullWidth: true,
        autoFocus: true,
        slotProps: {
          htmlInput: {
            inputMode: "numeric",
            pattern: "[0-9]*",
            autoComplete: "one-time-code",
            maxLength: CODE_LENGTH,
            "aria-label": intl.formatMessage({ id: "mfa.enrollCodeLabel" })
          }
        }
      }
    ),
    /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 1, justifyContent: "flex-end", children: [
      onCancel && /* @__PURE__ */ jsx(Button, { onClick: onCancel, disabled: pending, children: /* @__PURE__ */ jsx(FormattedMessage, { id: "mfa.enrollCancel" }) }),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "submit",
          variant: "contained",
          disabled: code.length !== CODE_LENGTH || pending,
          startIcon: pending ? /* @__PURE__ */ jsx(CircularProgress, { size: 16, color: "inherit" }) : void 0,
          children: /* @__PURE__ */ jsx(FormattedMessage, { id: pending ? "mfa.enrollVerifying" : "mfa.enrollVerify" })
        }
      )
    ] })
  ] }) });
}
function LoadingBlock({ label, py = 6, size }) {
  return /* @__PURE__ */ jsx(Box, { sx: { display: "flex", justifyContent: "center", py }, children: /* @__PURE__ */ jsx(CircularProgress, { "aria-label": label, ...size === void 0 ? {} : { size } }) });
}
function SubmitButton({
  pending = false,
  disabled,
  startIcon,
  children,
  ...rest
}) {
  return /* @__PURE__ */ jsx(
    Button,
    {
      ...rest,
      disabled: disabled || pending,
      "aria-busy": pending || void 0,
      startIcon: pending ? /* @__PURE__ */ jsx(CircularProgress, { size: 16, color: "inherit" }) : startIcon,
      children
    }
  );
}
function MetaList({ children }) {
  return /* @__PURE__ */ jsx(Box, { component: "dl", sx: { m: 0 }, children });
}
function MetaRow({ label, children, labelWidth = 160 }) {
  return /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-child": { borderBottom: 0 }
      },
      children: [
        /* @__PURE__ */ jsx(
          Typography,
          {
            component: "dt",
            variant: "caption",
            sx: {
              width: { xs: "auto", sm: labelWidth },
              flexShrink: 0,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontWeight: 600,
              py: { xs: 0, sm: 0.5 }
            },
            children: label
          }
        ),
        /* @__PURE__ */ jsx(Box, { component: "dd", sx: { flexGrow: 1, m: 0, minWidth: 0, wordBreak: "break-word" }, children })
      ]
    }
  );
}
function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onClose,
  pending = false,
  destructive = true,
  confirmDisabled = false,
  error
}) {
  const titleId = useId();
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      open,
      onClose: () => {
        if (!pending) onClose();
      },
      "aria-labelledby": titleId,
      children: [
        /* @__PURE__ */ jsx(DialogTitle, { id: titleId, children: title }),
        /* @__PURE__ */ jsx(DialogContent, { children: /* @__PURE__ */ jsxs(Stack, { spacing: 2, children: [
          error !== void 0 && error !== null && error !== false && /* @__PURE__ */ jsx(Alert, { severity: "error", role: "alert", children: error }),
          /* @__PURE__ */ jsx(DialogContentText, { children: body })
        ] }) }),
        /* @__PURE__ */ jsxs(DialogActions, { children: [
          /* @__PURE__ */ jsx(Button, { onClick: onClose, disabled: pending, children: cancelLabel }),
          /* @__PURE__ */ jsx(
            SubmitButton,
            {
              variant: "contained",
              color: destructive ? "error" : "primary",
              onClick: onConfirm,
              pending,
              disabled: confirmDisabled,
              children: confirmLabel
            }
          )
        ] })
      ]
    }
  );
}
var NON_DEPLOY_VERSIONS = /* @__PURE__ */ new Set(["", "dev", "development"]);
function VersionUpdateBanner({
  currentVersion,
  versionUrl = "/version.json",
  pollIntervalMs = 6e4,
  message = "A new version of this app is available.",
  refreshLabel = "Refresh",
  dismissLabel = "Dismiss"
}) {
  const [status, setStatus] = useState("idle");
  const currentVersionRef = useRef(currentVersion);
  currentVersionRef.current = currentVersion;
  const enabled = !NON_DEPLOY_VERSIONS.has(currentVersion);
  const check = useCallback(async () => {
    try {
      const res = await fetch(versionUrl, { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      const raw = data && typeof data === "object" && "version" in data ? data.version : void 0;
      const deployed = String(raw ?? "");
      if (deployed && deployed !== currentVersionRef.current) {
        setStatus("available");
      }
    } catch {
    }
  }, [versionUrl]);
  useEffect(() => {
    if (!enabled || status !== "idle") return;
    let cancelled = false;
    const runCheck = () => {
      if (!cancelled) void check();
    };
    const intervalId = setInterval(runCheck, pollIntervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") runCheck();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", runCheck);
    runCheck();
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", runCheck);
    };
  }, [enabled, status, pollIntervalMs, check]);
  if (status !== "available") return null;
  return /* @__PURE__ */ jsx(
    Snackbar,
    {
      open: true,
      anchorOrigin: { vertical: "bottom", horizontal: "center" },
      children: /* @__PURE__ */ jsx(
        Alert,
        {
          severity: "info",
          variant: "filled",
          action: /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Button, { color: "inherit", size: "small", onClick: () => window.location.reload(), children: refreshLabel }),
            /* @__PURE__ */ jsx(Button, { color: "inherit", size: "small", onClick: () => setStatus("dismissed"), children: dismissLabel })
          ] }),
          children: message
        }
      )
    }
  );
}

// src/lib/apiError.ts
function statusCodeOf(err) {
  if (typeof err === "object" && err !== null && "statusCode" in err) {
    const code = err.statusCode;
    if (typeof code === "number") return code;
  }
  return void 0;
}
function isVersionConflict(err) {
  return statusCodeOf(err) === 409;
}
function errorCodeOf(err) {
  if (typeof err !== "object" || err === null || !("body" in err)) return void 0;
  const body = err.body;
  if (typeof body !== "object" || body === null || !("errorCode" in body)) return void 0;
  const errorCode = body.errorCode;
  return typeof errorCode === "string" && errorCode !== "" ? errorCode : void 0;
}
function extractRequestId(err) {
  if (typeof err !== "object" || err === null || !("body" in err)) return void 0;
  const body = err.body;
  if (typeof body !== "object" || body === null || !("requestId" in body)) return void 0;
  const requestId = body.requestId;
  return typeof requestId === "string" && requestId !== "" ? requestId : void 0;
}
function extractErrorMessage(err) {
  if (typeof err !== "object" || err === null || !("body" in err)) return void 0;
  const body = err.body;
  if (typeof body !== "object" || body === null || !("message" in body)) return void 0;
  const message = body.message;
  return typeof message === "string" && message.trim() !== "" ? message.trim() : void 0;
}
function RequestIdCaption({ error }) {
  const requestId = extractRequestId(error);
  if (requestId === void 0) return null;
  return /* @__PURE__ */ jsx(
    Typography,
    {
      variant: "caption",
      component: "p",
      sx: { mt: 0.5, opacity: 0.85, fontFamily: "monospace" },
      children: /* @__PURE__ */ jsx(FormattedMessage, { id: "error.requestId", values: { requestId } })
    }
  );
}
function ApiErrorAlert({
  error,
  children,
  severity = "error",
  action,
  sx
}) {
  return /* @__PURE__ */ jsxs(Alert, { severity, role: "alert", ...action ? { action } : {}, ...sx ? { sx } : {}, children: [
    children,
    /* @__PURE__ */ jsx(RequestIdCaption, { error })
  ] });
}
function SearchModeToggle({
  value,
  onChange,
  labels,
  ariaLabel,
  disabled
}) {
  return /* @__PURE__ */ jsxs(
    ToggleButtonGroup,
    {
      value,
      exclusive: true,
      size: "small",
      disabled,
      onChange: (_e, next) => {
        if (next !== null) onChange(next);
      },
      "aria-label": ariaLabel,
      children: [
        /* @__PURE__ */ jsx(ToggleButton, { value: "HYBRID", children: labels.hybrid }),
        /* @__PURE__ */ jsx(ToggleButton, { value: "SEMANTIC", children: labels.semantic }),
        /* @__PURE__ */ jsx(ToggleButton, { value: "TEXT", children: labels.keyword })
      ]
    }
  );
}

// src/components/search/safeHref.ts
var ALLOWED_PREFIX = /^(?:https?:|mailto:|#)/i;
var SITE_ORIGIN = "https://site.invalid";
function isSafeHref(href) {
  if (typeof href !== "string") return false;
  if (ALLOWED_PREFIX.test(href)) return true;
  if (!href.startsWith("/")) return false;
  try {
    return new URL(href, SITE_ORIGIN).origin === SITE_ORIGIN;
  } catch {
    return false;
  }
}
function SearchResultCard({
  title,
  href,
  linkComponent,
  typeLabel,
  typeColor = "primary",
  extraChips,
  snippet,
  dateLabel,
  similarityPercent,
  similarityLabel,
  idCaption
}) {
  const hasSimilarity = typeof similarityPercent === "number" && similarityPercent > 0;
  return /* @__PURE__ */ jsx(Card, { variant: "outlined", children: /* @__PURE__ */ jsxs(CardContent, { children: [
    /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 1, alignItems: "flex-start", justifyContent: "space-between", children: [
      /* @__PURE__ */ jsxs(Box, { sx: { minWidth: 0 }, children: [
        /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", sx: { mb: 0.5, flexWrap: "wrap" }, useFlexGap: true, children: [
          /* @__PURE__ */ jsx(Chip, { size: "small", color: typeColor, variant: "outlined", label: typeLabel }),
          extraChips
        ] }),
        href && isSafeHref(href) ? /* @__PURE__ */ jsx(
          Link,
          {
            ...linkComponent ? { component: linkComponent } : {},
            ...linkComponent ? { to: href } : { href },
            variant: "subtitle1",
            sx: { fontWeight: 600 },
            children: title
          }
        ) : /* @__PURE__ */ jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 600 }, children: title }),
        idCaption && /* @__PURE__ */ jsx(
          Typography,
          {
            variant: "caption",
            color: "text.secondary",
            sx: { display: "block", fontFamily: "monospace" },
            children: idCaption
          }
        )
      ] }),
      hasSimilarity && /* @__PURE__ */ jsx(Tooltip, { title: similarityLabel ?? "", children: /* @__PURE__ */ jsx(Chip, { size: "small", color: "default", label: `${Math.round(similarityPercent)}%`, sx: { flexShrink: 0 } }) })
    ] }),
    snippet && snippet.length > 0 && /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 1 }, children: snippet }),
    dateLabel && /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "text.secondary", sx: { mt: 1, display: "block" }, children: dateLabel })
  ] }) });
}

// src/schema-ui/schemaSurfaces.ts
function schemasForSurface(schemas, surface) {
  return schemas.filter(
    (s) => typeof s.typeName === "string" && (s.allowedSurfaces ?? []).some((declared) => declared === surface)
  );
}
function distinctTypes(schemas) {
  const seen = /* @__PURE__ */ new Set();
  const result = [];
  for (const s of schemas) {
    if (seen.has(s.typeName)) continue;
    seen.add(s.typeName);
    result.push(s);
  }
  return result;
}

// src/schema-ui/recordForm.ts
function nonEmpty(value) {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : void 0;
}
function fieldLabel(field, hints) {
  return nonEmpty(hints?.[field.fieldId]?.label) ?? field.fieldId;
}
function fieldHelpText(field, hints) {
  return nonEmpty(hints?.[field.fieldId]?.helpText) ?? field.description;
}
function fieldWidget(field, hints) {
  return nonEmpty(hints?.[field.fieldId]?.widget);
}
function orderedFormFields(fields, hints) {
  return fields.filter(isFormEditable).map((field, index) => ({ field, index, order: hints?.[field.fieldId]?.order })).sort((a, b) => {
    const ao = a.order ?? Number.POSITIVE_INFINITY;
    const bo = b.order ?? Number.POSITIVE_INFINITY;
    return ao !== bo ? ao - bo : a.index - b.index;
  }).map((entry) => entry.field);
}
function groupFieldsBySection(orderedFields, hints) {
  const groups = [];
  const byKey = /* @__PURE__ */ new Map();
  for (const field of orderedFields) {
    const section = nonEmpty(hints?.[field.fieldId]?.section);
    const key = section ?? "";
    let group = byKey.get(key);
    if (!group) {
      group = { section, fields: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.fields.push(field);
  }
  return groups;
}
var FORM_EDITABLE_FIELD_TYPES = /* @__PURE__ */ new Set([
  "string",
  "number",
  "boolean",
  "date",
  "datetime",
  "enum"
]);
function temporalInputKind(field, hints) {
  if (field.fieldType === "datetime") return "datetime";
  if (field.fieldType !== "date") return null;
  if (fieldWidget(field, hints) === "date") return "date";
  return "datetime";
}
function toDateInputValue(value) {
  if (typeof value !== "string") return "";
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  return match?.[1] ?? "";
}
function toDateTimeLocalInputValue(value) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  const dateOnly = /^(\d{4}-\d{2}-\d{2})$/.exec(trimmed);
  if (dateOnly) return `${dateOnly[1] ?? ""}T00:00`;
  const dateTime = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(trimmed);
  if (dateTime) return `${dateTime[1] ?? ""}T${dateTime[2] ?? ""}`;
  return "";
}
function isFormEditable(field) {
  return FORM_EDITABLE_FIELD_TYPES.has(field.fieldType);
}
var RESERVED_PAYLOAD_KEYS = /* @__PURE__ */ new Set([
  "externalId",
  "partnerUserId",
  "userId",
  "scopes"
]);
function isReservedPayloadKey(key) {
  return RESERVED_PAYLOAD_KEYS.has(key);
}
function stripReservedPayloadKeys(payload) {
  const next = {};
  for (const [key, value] of Object.entries(payload)) {
    if (!RESERVED_PAYLOAD_KEYS.has(key)) next[key] = value;
  }
  return next;
}
function coerceFieldValue(field, input) {
  if (field.fieldType === "boolean") {
    return typeof input === "boolean" ? input : input === "true";
  }
  const text = typeof input === "string" ? input : String(input);
  if (field.fieldType === "number") {
    if (text.trim() === "") return void 0;
    const n = Number(text);
    return Number.isNaN(n) ? text : n;
  }
  if (text === "" && field.fieldType !== "string") return void 0;
  return text;
}
function describedKeys(fields) {
  return new Set(fields.map((f) => f.fieldId));
}
function unstructuredKeys(fields, payload) {
  const described = describedKeys(fields);
  return Object.keys(payload).filter((k) => !described.has(k)).sort();
}
function isPresent(value) {
  return value !== void 0 && value !== null && value !== "";
}
function enumOptions(field) {
  if (!field.enumValues) return [];
  return field.enumValues.map((entry) => {
    if (entry && typeof entry === "object" && "value" in entry) {
      return String(entry.value);
    }
    return String(entry);
  }).filter((v) => v !== "undefined" && v !== "");
}
function validateFields(fields, payload) {
  const errors = {};
  for (const field of fields) {
    if (!isFormEditable(field)) continue;
    const value = payload[field.fieldId];
    if (field.required && !isPresent(value)) {
      errors[field.fieldId] = "required";
      continue;
    }
    if (field.fieldType === "enum" && isPresent(value)) {
      const options = enumOptions(field);
      if (options.length > 0 && !options.includes(String(value))) {
        errors[field.fieldId] = "enum";
      }
    }
  }
  return errors;
}
function withField(payload, fieldId, value) {
  const next = { ...payload };
  if (value === void 0) {
    delete next[fieldId];
  } else {
    next[fieldId] = value;
  }
  return next;
}

// src/schema-ui/recordColumns.ts
var DEFAULT_COLUMN_CAP = 4;
function findDisplayFieldId(fields, hints) {
  if (!hints) return void 0;
  const flagged = fields.map((field, index) => ({ field, index, order: hints[field.fieldId]?.order })).filter((e) => hints[e.field.fieldId]?.displayField === true).sort((a, b) => {
    const ao = a.order ?? Number.POSITIVE_INFINITY;
    const bo = b.order ?? Number.POSITIVE_INFINITY;
    return ao !== bo ? ao - bo : a.index - b.index;
  });
  return flagged[0]?.field.fieldId;
}
function deriveValueColumns(fields, hints, cap = DEFAULT_COLUMN_CAP, excludeFieldId) {
  return orderedFormFields(fields, hints).filter((field) => field.fieldId !== excludeFieldId).slice(0, Math.max(0, cap)).map((field) => ({
    fieldId: field.fieldId,
    label: fieldLabel(field, hints),
    fieldType: field.fieldType,
    filterable: field.filterable === true,
    inline: field.inline === true
  }));
}
function filterableFieldIds(fields) {
  return fields.filter((f) => f.filterable === true).map((f) => f.fieldId);
}
function isBlank(value) {
  return value === void 0 || value === null || value === "";
}
function formatCellValue(value) {
  if (isBlank(value)) return "\u2014";
  if (typeof value === "boolean") return value ? "\u2713" : "\u2014";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}
function compareValues(a, b) {
  const ab = isBlank(a);
  const bb = isBlank(b);
  if (ab || bb) return ab === bb ? 0 : ab ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return a === b ? 0 : a ? -1 : 1;
  return String(a).localeCompare(String(b));
}
function sortRecords(records, accessor, direction) {
  const sign = direction === "desc" ? -1 : 1;
  return records.map((record, index) => ({ record, index })).sort((x, y) => {
    const cmp = compareValues(accessor(x.record), accessor(y.record));
    return cmp !== 0 ? sign * cmp : x.index - y.index;
  }).map((entry) => entry.record);
}
function payloadMatchesQuery(payload, query, fieldIds) {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  if (!payload) return false;
  return fieldIds.some((id) => {
    const value = payload[id];
    return !isBlank(value) && formatCellValue(value).toLowerCase().includes(q);
  });
}
function RecordFormFields({
  fields,
  value,
  errors,
  renderHints,
  rawOnlyNoteId,
  disabled = false,
  onChange
}) {
  const intl = useIntl();
  const handleChange = disabled ? () => {
  } : onChange;
  const sections = groupFieldsBySection(orderedFormFields(fields, renderHints), renderHints);
  const complexFieldIds = fields.filter((f) => !isFormEditable(f)).map((f) => f.fieldId);
  const extras = unstructuredKeys(fields, value);
  const rawOnlyKeys = [...complexFieldIds, ...extras];
  const helperFor = (field) => {
    if (errors[field.fieldId] === "required") {
      return intl.formatMessage({ id: "recordForm.errorRequired" });
    }
    if (errors[field.fieldId] === "enum") {
      return intl.formatMessage({ id: "recordForm.errorEnum" });
    }
    return fieldHelpText(field, renderHints);
  };
  const renderField = (field) => {
    const hasError = field.fieldId in errors;
    const helper = helperFor(field);
    const current = value[field.fieldId];
    const label = fieldLabel(field, renderHints);
    const labelWithReq = `${label}${field.required ? " *" : ""}`;
    if (field.fieldType === "boolean") {
      return /* @__PURE__ */ jsxs(Box, { children: [
        /* @__PURE__ */ jsx(
          FormControlLabel,
          {
            control: /* @__PURE__ */ jsx(
              Switch,
              {
                checked: current === true,
                disabled,
                onChange: (e) => handleChange(field, e.target.checked)
              }
            ),
            label
          }
        ),
        helper && /* @__PURE__ */ jsx(FormHelperText, { error: hasError, children: helper })
      ] }, field.fieldId);
    }
    if (field.fieldType === "enum") {
      const options = enumOptions(field);
      return /* @__PURE__ */ jsxs(
        FormControl,
        {
          size: "small",
          error: hasError,
          disabled,
          sx: { maxWidth: 480 },
          children: [
            /* @__PURE__ */ jsx(InputLabel, { id: `field-${field.fieldId}-label`, children: labelWithReq }),
            /* @__PURE__ */ jsxs(
              Select,
              {
                labelId: `field-${field.fieldId}-label`,
                label: labelWithReq,
                value: current === void 0 || current === null ? "" : String(current),
                onChange: (e) => handleChange(field, e.target.value),
                children: [
                  !field.required && /* @__PURE__ */ jsx(MenuItem, { value: "", children: /* @__PURE__ */ jsx("em", { children: /* @__PURE__ */ jsx(FormattedMessage, { id: "recordForm.enumNone" }) }) }),
                  options.map((opt) => /* @__PURE__ */ jsx(MenuItem, { value: opt, children: opt }, opt))
                ]
              }
            ),
            helper && /* @__PURE__ */ jsx(FormHelperText, { children: helper })
          ]
        },
        field.fieldId
      );
    }
    const temporal = temporalInputKind(field, renderHints);
    const isTextarea = field.fieldType === "string" && fieldWidget(field, renderHints) === "textarea";
    const inputType = temporal === "datetime" ? "datetime-local" : temporal === "date" ? "date" : field.fieldType === "number" ? "number" : "text";
    const displayValue = temporal === "datetime" ? toDateTimeLocalInputValue(current) : temporal === "date" ? toDateInputValue(current) : current === void 0 || current === null ? "" : String(current);
    return /* @__PURE__ */ jsx(
      TextField,
      {
        label: labelWithReq,
        type: isTextarea ? void 0 : inputType,
        multiline: isTextarea,
        minRows: isTextarea ? 3 : void 0,
        value: displayValue,
        onChange: (e) => handleChange(field, e.target.value),
        error: hasError,
        helperText: helper,
        disabled,
        size: "small",
        sx: { maxWidth: 480 },
        slotProps: temporal !== null ? { inputLabel: { shrink: true } } : void 0
      },
      field.fieldId
    );
  };
  return /* @__PURE__ */ jsxs(Stack, { spacing: 3, children: [
    sections.map((group) => /* @__PURE__ */ jsxs(Stack, { spacing: 3, children: [
      group.section && /* @__PURE__ */ jsx(Typography, { variant: "subtitle2", sx: { fontWeight: 700, color: "text.secondary" }, children: group.section }),
      group.fields.map(renderField)
    ] }, group.section ?? "__nosection__")),
    rawOnlyKeys.length > 0 && /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "text.secondary", sx: { display: "block", mb: 0.5 }, children: /* @__PURE__ */ jsx(FormattedMessage, { id: rawOnlyNoteId ?? "recordForm.rawOnlyNote" }) }),
      /* @__PURE__ */ jsx(Stack, { direction: "row", spacing: 1, useFlexGap: true, sx: { flexWrap: "wrap" }, children: rawOnlyKeys.map((k) => /* @__PURE__ */ jsx(Chip, { label: k, size: "small", variant: "outlined" }, k)) })
    ] })
  ] });
}

// src/inference/inferenceStream.ts
var INITIAL_INFERENCE_STREAM_STATE = {
  status: "idle",
  text: ""
};
function startedInferenceStreamState() {
  return { status: "streaming", text: "" };
}
function reduceInferenceEvent(state, event) {
  switch (event.event) {
    case "content_delta":
      return { ...state, status: "streaming", text: state.text + (event.delta ?? "") };
    case "reasoning_delta":
      return state;
    case "search_results":
      return {
        ...state,
        citations: event.results,
        search: { totalResults: event.totalResults, searchTimeMs: event.searchTimeMs }
      };
    case "truncation_warning":
      return { ...state, truncation: event };
    case "document_context":
      return { ...state, documentContext: event };
    case "done":
      return { ...state, status: "done", usage: event };
    case "error":
      return { ...state, status: "error", error: { message: event.message, code: event.code } };
    default:
      return state;
  }
}
function useInferenceStream() {
  const [state, setState] = useState(INITIAL_INFERENCE_STREAM_STATE);
  const controllerRef = useRef(null);
  const runIdRef = useRef(0);
  const stop = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    runIdRef.current += 1;
  }, []);
  const run = useCallback(
    (runner) => {
      stop();
      const controller = new AbortController();
      controllerRef.current = controller;
      runIdRef.current += 1;
      const myRunId = runIdRef.current;
      setState(startedInferenceStreamState());
      void (async () => {
        try {
          const stream = await runner({ abortSignal: controller.signal });
          for await (const event of stream) {
            if (runIdRef.current !== myRunId) return;
            setState((prev) => reduceInferenceEvent(prev, event));
          }
        } catch (err) {
          if (runIdRef.current !== myRunId) return;
          const message = err instanceof Error ? err.message : "Inference stream failed.";
          const status = statusCodeOf(err);
          const errorCode = errorCodeOf(err);
          const detail = extractErrorMessage(err);
          const error = {
            message,
            code: "stream_error",
            ...status !== void 0 && { status },
            ...errorCode !== void 0 && { errorCode },
            ...detail !== void 0 && { detail }
          };
          setState((prev) => prev.status === "done" ? prev : { ...prev, status: "error", error });
        }
      })();
    },
    [stop]
  );
  const cancel = useCallback(() => {
    stop();
    setState((prev) => prev.status === "streaming" ? { ...prev, status: "idle" } : prev);
  }, [stop]);
  const reset = useCallback(() => {
    stop();
    setState(INITIAL_INFERENCE_STREAM_STATE);
  }, [stop]);
  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    []
  );
  return { state, isStreaming: state.status === "streaming", run, cancel, reset };
}
var I18N_DEFAULT_LOCALE = "en";
function detectLocale(messagesByLocale, defaultLocale) {
  if (typeof navigator === "undefined") {
    return defaultLocale;
  }
  const language = navigator.language.split("-")[0] ?? defaultLocale;
  return language in messagesByLocale ? language : defaultLocale;
}
function IntlProvider({
  children,
  messagesByLocale,
  defaultLocale = I18N_DEFAULT_LOCALE,
  locale
}) {
  const activeLocale = locale ?? detectLocale(messagesByLocale, defaultLocale);
  const messages = messagesByLocale[activeLocale] ?? messagesByLocale[defaultLocale] ?? {};
  return /* @__PURE__ */ jsx(IntlProvider$1, { locale: activeLocale, defaultLocale, messages, children });
}

// src/index.ts
var baseMessagesEn = messages_en_default;

export { ApiErrorAlert, AppLayout, AuthCard, AuthProvider, ConfirmDialog, CurrentTenantProvider, DEFAULT_COLUMN_CAP, ErrorBoundary, FORM_EDITABLE_FIELD_TYPES, I18N_DEFAULT_LOCALE, INITIAL_INFERENCE_STREAM_STATE, IntlProvider, LoadingBlock, MetaList, MetaRow, PasswordField, RecordFormFields, RequestIdCaption, RequireAuth, RequireScope, ScopeGate, SearchModeToggle, SearchResultCard, SubmitButton, TotpEnrollmentWizard, VersionUpdateBanner, assertEmbeddedAuth, assertHostedAuth, authErrorToMessage, baseMessagesEn, coerceFieldValue, compareValues, deriveValueColumns, distinctTypes, enumOptions, errorCodeOf, extractErrorMessage, extractRequestId, fieldHelpText, fieldLabel, fieldWidget, filterableFieldIds, findDisplayFieldId, formatCellValue, groupFieldsBySection, isFormEditable, isReservedPayloadKey, isVersionConflict, orderedFormFields, payloadMatchesQuery, reduceInferenceEvent, schemasForSurface, sortRecords, startedInferenceStreamState, statusCodeOf, stripReservedPayloadKeys, temporalInputKind, toDateInputValue, toDateTimeLocalInputValue, unstructuredKeys, useActiveTenantId, useAuth, useCurrentTenant, useInferenceStream, useScopeGate, validateFields, withField };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map